import { SiteMapResponse } from '../../models/siteMapResponse';
import { Topic } from '../../models/topic';
import {
    ASTUTE,
    BRAND_ATTRIBUTES,
    DESCRIPTION_ELLIPSIS,
    GREEK_LANGUAGE_CODE,
    RTE_SITEMAP_FETCH_SIZE,
    RTE_SITEMAP_INITIAL_TOTAL_PAGES,
    RTE_SITEMAP_PAGE_SIZE,
    RTE_SITEMAP_PAGE_START,
    SUPPORT_HUB_PREFIXES_NOT_FOR_BBT,
    SUPPORT_HUB_PREFIXES_NOT_FOR_SITEMAP,
    SUPPORT_HUB_REGEX,
} from '../../support-constants';
import siteMapService from '../site-map-service/site-map-service';
import xss from 'xss';
import { PlaceholderResponse } from '../../models/place-holder-response';
import { TopicPage } from '../../models/topic-page';
import { HttpsProxyAgent } from 'https-proxy-agent';
import { SuggestedTopic } from '../../models/suggested-topic';
import cacheService from '../../../services/cache-service/cache-service';
import { CategoryPage } from '../../models/category-page';
import { SubCategoryPage } from '../../models/sub-category-page';
import ServiceHandler from '../../../services/service-handler';
import serverSideService from '../../../services/server-side-service/server-side-service';
import HttpService from '../../../services/http-service/http-service';
import { Category } from '../../models/category';
import { CategoryValue } from '../../models/categoryValue';
import { CatSubcatFilteredResponse } from '../../models/cat-subcat-filtered-response';
import { RteBbtSubCategory } from '../../models/rte-category-bbt-response';
import slugify from 'slugify';
import RteService from './rte-service';
import EmplifiService from './emplifi-service';

const httpsAgent = new HttpsProxyAgent(
    process.env.http_proxy || 'http://internet.ford.com:83'
);

export interface AstuteCacheResponse {
    [prefix: string]: Topic[];
}

interface BrandAndLanguageRegionCode {
    languageRegionCode: string;
    brand: string;
}

const CACHE_TTL_MS = 5 * 60 * 1000;
const bbtTopicIdCache = new Map<string, { topicId: number; timestamp: number }>();

export function sanitizeGreekUrlSegment(value: string): string {

    const cleaned = value.replaceAll(/[+&®*™()/\u2013]/g, '');
    
    let end = cleaned.length;
    while (end > 0) {
        const char = cleaned.codePointAt(end - 1);
        if (char === 0x3b || char === 0x37e) {
            end--;
        } else {
            break;
        }
    }

    return cleaned.slice(0, end);
}

export function cacheBbtTopicIds(
    languageRegionCode: string,
    brand: string,
    categorySeoSlug: string,
    filteredCategories: CategoryPage | undefined
): void {
    if (!filteredCategories?.subCategories) return;
    const now = Date.now();
    for (const subCat of filteredCategories.subCategories) {
        if (!subCat.allTopics) continue;
        for (const topic of subCat.allTopics) {
            if (topic.topicId && topic.topicLabelSeo) {
                const key = `${languageRegionCode}|${brand}|${categorySeoSlug}|${subCat.subCategorySeo}|${topic.topicLabelSeo}`;
                bbtTopicIdCache.set(key, { topicId: topic.topicId, timestamp: now });
            }
        }
    }
}

export function getCachedTopicId(
    languageRegionCode: string,
    brand: string,
    categorySeoSlug: string,
    subcategorySeoSlug: string,
    topicLabelSeo: string
): number | undefined {
    const key = `${languageRegionCode}|${brand}|${categorySeoSlug}|${subcategorySeoSlug}|${topicLabelSeo}`;
    const cached = bbtTopicIdCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return cached.topicId;
    }
    return undefined;
}

const FAQ_SCRIPT_TAG_REGEX = /<script\b[^>]*>[\s\S]*?<\/script>/gi;
const FAQ_SCRIPT_OPEN_TAG_REGEX = /^<script\b[^>]*>/i;
const FAQ_SCRIPT_CLOSE_TAG_REGEX = /<\/script>$/i;
const JSON_LD_TYPE_REGEX = /type\s*=[^>]*application\/ld\+json/i;

export interface ExtractedFaqSchema {
    html: string;
    faqSchema: Record<string, unknown> | null;
}

function isFaqPageSchema(value: unknown): value is Record<string, unknown> {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        (value as Record<string, unknown>)['@type'] === 'FAQPage'
    );
}

export function extractFaqSchema(html: string): ExtractedFaqSchema {
    if (!html || typeof html !== 'string') {
        return { html: html ?? '', faqSchema: null };
    }

    for (const match of Array.from(html.matchAll(FAQ_SCRIPT_TAG_REGEX))) {
        const scriptTag = match[0];
        if (!JSON_LD_TYPE_REGEX.test(scriptTag)) {
            continue;
        }

        const jsonContent = scriptTag
            .replace(FAQ_SCRIPT_OPEN_TAG_REGEX, '')
            .replace(FAQ_SCRIPT_CLOSE_TAG_REGEX, '')
            .trim();

        try {
            const parsedJson = JSON.parse(jsonContent);
            if (isFaqPageSchema(parsedJson)) {
                return {
                    html: html.replace(scriptTag, ''),
                    faqSchema: parsedJson,
                };
            }
        } catch {
            continue;
        }
    }

    return { html, faqSchema: null };
}

/**
 * Escapes a previously-extracted FAQPage JSON-LD object for safe embedding in a
 * <script type="application/ld+json"> tag. `<`, `>` and `&` are unicode escaped
 * so that any user/AI-authored text within the schema (e.g. answer text) cannot
 * break out of the script tag or inject markup.
 */
export function serializeFaqSchemaJson(
    faqSchema: Record<string, unknown> | null
): string {
    if (!faqSchema) {
        return '';
    }

    return JSON.stringify(faqSchema)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026');
}

/**
 * Re-serializes a previously-extracted FAQPage JSON-LD object into a safe
 * <script type="application/ld+json"> tag.
 */
export function serializeFaqSchemaScript(
    faqSchema: Record<string, unknown> | null
): string {
    const escapedJson = serializeFaqSchemaJson(faqSchema);

    return escapedJson
        ? `<script type="application/ld+json">${escapedJson}</script>`
        : '';
}


export default class AstuteService {
    private emplifiService = new EmplifiService();
    private rteService = new RteService();

    private static readonly INVALID_TOPIC_PATTERNS: RegExp[] = [
        /\.[a-z0-9]{2,5}$/i,
        /^tel:/i,
        /\|/,
        /^[0-9a-f]{6,}$/i,
    ];

    private static isInvalidTopicLabel(topicLabel: string): boolean {
        return AstuteService.INVALID_TOPIC_PATTERNS.some(pattern =>
            pattern.test(topicLabel)
        );
    }

    private async resolveTopicId(
        languageRegionCode: string,
        brand: string,
        category: string,
        subCategory: string,
        topicLabel: string
    ): Promise<number | undefined> {
        const cachedId = getCachedTopicId(languageRegionCode, brand, category, subCategory, topicLabel);
        if (cachedId) {
            console.log(`[getTopicById] cache hit for topicId=${cachedId} topic=${topicLabel}`);
            return cachedId;
        }

        try {
            const topicIdStr = await this.rteService.getTopicIdByUrlParams(
                languageRegionCode,
                brand,
                topicLabel,
                category,
                subCategory
            );
            return topicIdStr ? parseInt(topicIdStr) : undefined;
        } catch (e) {
            console.error(
                `getTopicById - getTopicIdByLabel failed for ${topicLabel} - ${e}`
            );
            return undefined;
        }
    }

    private extractCategoryNames(topicData: any): { categoryName?: string; subCategoryName?: string } {
        if (!topicData.categories?.length) {
            return {};
        }

        const cat = topicData.categories[0];
        const categoryName = cat.localizedCategoryName?.replace(
            SUPPORT_HUB_REGEX.ARTICLE_CATEGORY_NAME_REGEX,
            ''
        );
        const subCategoryName = cat.categoryValues?.[0]?.localizedCategoryValue?.replace(
            SUPPORT_HUB_REGEX.ARTICLE_CATEGORY_NAME_REGEX,
            ''
        );
        return { categoryName, subCategoryName };
    }

    private async populateTopicPageFromData(
        topicPage: TopicPage,
        topicData: any,
        topicLabel: string,
        root: string,
        languageRegionCode: string,
        brand: string
    ): Promise<void> {
        const { categoryName, subCategoryName } = this.extractCategoryNames(topicData);
        topicPage.categoryName = categoryName;
        topicPage.subCategoryName = subCategoryName;

        topicData.response = this.buildAdditionalTopicUrlsFromPrivilegeContent(
            topicData,
            root,
            languageRegionCode,
            brand
        );

        topicPage.faqSchema = extractFaqSchema(topicData.response).faqSchema;
        const sanitizedHtml = this.sanitizeHtml(topicData.response);
        const topicDataSeparated = this.getTopicBodyWoHeading(sanitizedHtml);
        topicPage.bodyWoHeading = topicDataSeparated.topicData;
        topicPage.topicHeading = topicDataSeparated.topicHeading;
        topicPage.topicLabel =
            topicData.topicLabel ||
            topicDataSeparated.topicHeading
                ?.replace(SUPPORT_HUB_REGEX.ALL_MARKUP_TAGS, '')
                .trim() || topicLabel;

        let metaDescription;
        try {
            metaDescription = await this.getTopicAttributes(
                brand,
                topicPage.topicId,
                languageRegionCode
            );
        } catch (e) {
            console.error(
                `Inside getTopicById - getTopicAttributes - ${e} ${topicPage.topicId}`
            );
        }
        topicPage.metaDescription = this.validateForMetaDescription(
            metaDescription,
            topicPage.bodyWoHeading
        );
    }

    public async getTopicById(
        category: string,
        subCategory: string,
        topicLabel: string,
        languageRegionCode: string,
        domain: string,
        root: string,
        brand: string
    ): Promise<TopicPage> {
        const topicPage: TopicPage = {
            topicLabel: '',
            topicHeading: '',
            bodyWoHeading: '',
            metaDescription: '',
            hrefLangs: '',
            canonical: '',
            topicAbsoluteSeo: '',
            topicId: 0,
            sessionId: '',
        };

        if (AstuteService.isInvalidTopicLabel(topicLabel)) {
            console.error(
                `getTopicById - Ignoring invalid topic label: ${topicLabel}`
            );
            topicPage.error = `Invalid topic label: ${topicLabel}`;
            return topicPage;
        }

        try {
            const sessionID = await this.getSessionIdForSearch(
                languageRegionCode,
                brand
            );

            const topicId = await this.resolveTopicId(
                languageRegionCode, brand, category, subCategory, topicLabel
            );

            if (!topicId) {
                topicPage.error = `Topic not found for: ${topicLabel}`;
                return topicPage;
            }

            topicPage.topicId = topicId;
            topicPage.sessionId = sessionID;
            const topicData = await this.astuteCallByTopicId(
                topicId,
                languageRegionCode,
                brand,
                sessionID
            );

            if (topicData) {
                await this.populateTopicPageFromData(
                    topicPage, topicData, topicLabel, root, languageRegionCode, brand
                );
            }

            const translatedRoute =
                siteMapService.getPathFromRouterConfigs(
                    languageRegionCode,
                    brand,
                    'AstuteTopic',
                    [category, subCategory, topicLabel]
                );

            const currentRoot = root.substring(0, root.length - 1);
            const link = `https://${domain}${currentRoot}${translatedRoute}`;
            topicPage.bodyWoHeading = topicPage.bodyWoHeading?.replace(
                SUPPORT_HUB_REGEX.HREF_HASH,
                `href="${link}#`
            );
            topicPage.canonical = link;

            const topicHrefLangs = await this.getHrefLangsForSupportedMarkets(
                topicPage.topicId,
                brand,
                languageRegionCode,
                [category, subCategory, topicLabel]
            );
            topicPage.hrefLangs = topicHrefLangs.join(' ');

            return topicPage;
        } catch (error) {
            console.error(
                `getTopicById - ${error} ${category} ${subCategory} ${topicLabel}`
            );
            throw new Error(`getTopicById - ${error}`);
        }
    }

    public async getTopicContentByTopicId(
        topic: Topic,
        siteMapData: Topic[],
        languageRegionCode: string,
        root: string,
        sessionID: string,
        brand: string,
        isSearch: boolean
    ) {
        const topicPage: TopicPage = {
            topicLabel: '',
            categoryName: '',
            subCategoryName: '',
            topicHeading: '',
            bodyWoHeading: '',
            metaDescription: '',
            hrefLangs: '',
            topicId: 0,
        };
        try {
            topicPage.topicId = topic.topicId;
            const topicSplit = topic.topicAbsoluteSeo?.split('/');
            topicPage.topicLabel = topic.topicLabel;
            topicPage.noIndex = topic.noIndex;
            topic.categories?.forEach(category => {
                this.assignCategoryNameForTopicPage(
                    topicSplit,
                    category,
                    topicPage
                );
            });

            if (!isSearch) {
                const topicseo = await this.getHrefLangsForSupportedMarkets(
                    topicPage.topicId,
                    brand,
                    languageRegionCode,
                    topic.topicAbsoluteSeo?.substring(1).split('/')
                );
                topicPage.hrefLangs = topicseo && topicseo.join(' ');
            }
            const topicData = await this.astuteCallByTopicId(
                topicPage.topicId,
                languageRegionCode,
                brand,
                sessionID
            );
            if (topicData) {
                topicData.response = await this.buildAdditionalTopicUrls(
                    topicData,
                    siteMapData,
                    root,
                    languageRegionCode,
                    brand
                );
                const sanitizedHtml = this.sanitizeHtml(topicData.response);
                const topicDataSeparated = this.getTopicBodyWoHeading(
                    sanitizedHtml
                );
                topicPage.bodyWoHeading = topicDataSeparated.topicData;
                topicPage.topicHeading = topicDataSeparated.topicHeading;
                let metaDescription;
                try {
                    metaDescription = await this.getTopicAttributes(
                        brand,
                        topicPage.topicId,
                        languageRegionCode
                    );
                } catch (e) {
                    console.error(
                        `Inside getTopicContentByTopicId - getTopicAttributes - ${e} ${topic.topicId}`
                    );
                }
                topicPage.metaDescription = this.validateForMetaDescription(
                    metaDescription,
                    topicPage.bodyWoHeading
                );
                this.rteArticleSearch(
                    topicPage.topicLabel || 'Default',
                    sessionID,
                    brand,
                    languageRegionCode,
                    topicPage.topicId
                ).then(searchResult => {
                    if (searchResult?.status && searchResult.status != 1) {
                        console.error(
                            'Invalid status',
                            searchResult.status,
                            'for topic id & label',
                            topicPage.topicId,
                            topicPage.topicLabel
                        );
                    }
                });
            }
            return topicPage;
        } catch (error) {
            console.error(
                `getTopicContentByTopicId - ${error} ${topic.topicId}`
            );
            throw new Error(`getTopicContentByTopicId - ${error}`);
        }
    }

    private getTopicAbsoluteSeo(topic: Topic) {
        return topic.topicAbsoluteSeo || '';
    }

    private getTopicName(topic: Topic) {
        return topic.topicName || '';
    }

    private getTopicLabelSeo(topic: Topic) {
        return topic.topicLabelSeo || '';
    }

    private getOldTopicLabels(topic: Topic) {
        return topic.oldTopicLabels || [];
    }

    private validateForMetaDescription(
        metaDescription: any,
        topicPageBodyWoHeading: string
    ) {
        return metaDescription?.Description
            ? metaDescription?.Description
            : this.getMetaDescription(topicPageBodyWoHeading) || '';
    }

    private assignCategoryNameForTopicPage(
        topicSplit: string[] | undefined,
        category: Category,
        topicPage: TopicPage
    ) {
        if (topicSplit && category.localizedCategoryNameSeo === topicSplit[1]) {
            topicPage.categoryName = category.localizedCategoryName;

            category.categoryValues.forEach(subCategory => {
                this.assignSubCategoryNameForTopicPage(
                    topicSplit,
                    subCategory,
                    topicPage
                );
            });
        }
    }

    private assignSubCategoryNameForTopicPage(
        topicSplit: string[] | undefined,
        subCategory: CategoryValue,
        topicPage: TopicPage
    ) {
        if (
            topicSplit &&
            subCategory.localizedCategoryValueSeo === topicSplit[2]
        )
            topicPage.subCategoryName = subCategory.localizedCategoryValue;
    }

    public async buildAdditionalTopicUrls(
        topicData: PlaceholderResponse,
        siteMapData: Topic[],
        root: string,
        languageRegionCode: string,
        brand: string
    ): Promise<string> {
        let markup = topicData.response;

        topicData.embeddedContents.forEach(content => {
            if (content.type === 'TOPIC') {
                const topic = siteMapData.find(
                    topic => topic.topicId.toString() === content.contentId
                );

                const currentRoot = root.substring(0, root.length - 1);
                const topicAbsoluteSeo =
                    topic && topic.topicAbsoluteSeo?.endsWith('/') ? '/' : '';
                const url = topic
                    ? `${currentRoot}${siteMapService
                          .getPathFromRouterConfigs(
                              languageRegionCode,
                              brand,
                              'AstuteTopic',
                              topic.topicAbsoluteSeo?.substring(1).split('/') ||
                                  []
                          )
                          ?.concat(topicAbsoluteSeo)}`
                    : `${currentRoot}${siteMapService.getPathFromRouterConfigs(
                          languageRegionCode,
                          brand,
                          'AstuteSearch',
                          [content.text]
                      )}`;
                const snippet = `<a href="${url}">${content.text}</a>`;
                markup = markup.replace(content.snippet, snippet);
            }
        });
        return markup;
    }

    public buildAdditionalTopicUrlsFromPrivilegeContent(
        topicData: PlaceholderResponse,
        root: string,
        languageRegionCode: string,
        brand: string
    ): string {
        let markup = topicData.response;
        const currentRoot = root.substring(0, root.length - 1);
        const languageCode = languageRegionCode.split('-')[0];

        topicData.embeddedContents.forEach(content => {
            if (content.type === 'TOPIC') {
                let url: string | undefined;
                if (content.categories && content.categories.length > 0) {
                    const category = content.categories[0];
                    const categoryValue =
                        category.categoryValues && category.categoryValues.length > 0
                            ? category.categoryValues[0]
                            : null;

                    if (categoryValue && content.topicLabel) {
                        const strippedCategoryName = (category.localizedCategoryName || category.categoryName)
                            .replace(SUPPORT_HUB_REGEX.ARTICLE_CATEGORY_NAME_REGEX, '');
                        const categorySeo = this.sanitizeString(
                            strippedCategoryName,
                            languageCode
                        );
                        const subCategorySeo = this.sanitizeString(
                            categoryValue.localizedCategoryValue,
                            languageCode
                        );
                        const topicLabelSeo = this.sanitizeString(
                            content.topicLabel,
                            languageCode
                        );
                        const topicAbsoluteSeo = `/${categorySeo}/${subCategorySeo}/${topicLabelSeo}`;
                        const topicPath = siteMapService
                            .getPathFromRouterConfigs(
                                languageRegionCode,
                                brand,
                                'AstuteTopic',
                                topicAbsoluteSeo.substring(1).split('/')
                            )
                            ?.concat('');

                        if (topicPath) {
                            url = `${currentRoot}${topicPath}`;
                        }
                    }
                }
                if (!url) {
                    const searchPath = siteMapService.getPathFromRouterConfigs(
                        languageRegionCode,
                        brand,
                        'AstuteSearch',
                        [content.text]
                    );
                    url = `${currentRoot}${searchPath}`;
                }

                const snippet = `<a href="${url}">${content.text}</a>`;
                markup = markup.replace(content.snippet, snippet);
            }
        });
        return markup;
    }

    public sanitizeString(source: string, languageCode: string) {
        const copyRightsRemoved = source?.replace(
            SUPPORT_HUB_REGEX.COPYRIGHTS_TRADE_REGISTERED_MARKS,
            ''
        );
        const slugifiedUrl = ASTUTE.SKIP_SLUGIFY_LANGUAGES.includes(
            languageCode
        )
            ? copyRightsRemoved
                  ?.toLocaleLowerCase(languageCode)
                  ?.replace(SUPPORT_HUB_REGEX.SPACES, '-')
                  ?.replace(SUPPORT_HUB_REGEX.FORWARD_SLASH, '')
            : copyRightsRemoved &&
              slugify(copyRightsRemoved, {
                  locale: languageCode,
                  lower: true,
              });

        const normalizedUrl = slugifiedUrl
            ?.replace(SUPPORT_HUB_REGEX.PERIOD, '-')
            ?.replace(SUPPORT_HUB_REGEX.INVALID_CHARS, '');
        if (normalizedUrl && languageCode === GREEK_LANGUAGE_CODE) {
            return sanitizeGreekUrlSegment(normalizedUrl);
        }
        return normalizedUrl;
    }
    public sanitizeHtml(rawHtml: string): string {
        const { html } = extractFaqSchema(rawHtml);
        return xss(html, {
            onIgnoreTag(tag, html) {
                if (ASTUTE.WHITELIST_XSS_MARKUP.includes(tag)) {
                    return html;
                }
                return '';
            },
            onIgnoreTagAttr: function(tag, name, value) {
                if (ASTUTE.WHITELIST_XSS_ATTRIBUTES.includes(name)) {
                    return name + '="' + value + '"';
                }
            },
        });
    }

    public getMetaDescription(topicData: string): string {
        const metaDescription = topicData
            .replace(SUPPORT_HUB_REGEX.ALL_MARKUP_TAGS, '')
            .replace(SUPPORT_HUB_REGEX.FIND_ALL_TABS_NEW_LINE, '')
            .replace(SUPPORT_HUB_REGEX.NON_BREAKING_SPACE, ' ')
            .replace(SUPPORT_HUB_REGEX.FIND_FIRST_260_CHARS, '$1')
            .trim()
            .concat(DESCRIPTION_ELLIPSIS);
        return metaDescription;
    }
    public getTopicBodyWoHeading(topicData: string) {
        const astuteData = topicData;
        const headingTag =
            astuteData.indexOf(`<${ASTUTE.HEADING_TAG}>`) >= 0
                ? ASTUTE.HEADING_TAG
                : 'h4';
        const openIndex = astuteData.indexOf(`<${headingTag}>`);
        const closeIndex = astuteData.indexOf(`</${headingTag}>`);
        const markupTopicHeading =
            openIndex >= 0 && closeIndex >= 0
                ? astuteData.substring(
                      openIndex,
                      closeIndex + `</${headingTag}>`.length
                  )
                : '';

        return {
            topicHeading: markupTopicHeading,
            topicData: markupTopicHeading
                ? astuteData.replace(markupTopicHeading, '').trim()
                : astuteData.trim(),
        };
    }

    public async getSitemapDataFromCache(
        supportedMarket: string,
        prefixes: string[],
        brand: string,
        skipSitemapCheck?: boolean
    ): Promise<Topic[]> {
        const topics: Topic[][] = [];
        let cacheDataForPrefixes: Topic[][] = [];
        const redisCacheResponse = await this.getAstuteFromCacheForRegion(
            supportedMarket,
            brand
        );

        prefixes.forEach(prefix => {
            redisCacheResponse.forEach(
                cacheData =>
                    cacheData[prefix] &&
                    cacheDataForPrefixes.push(cacheData[prefix])
            );
        });

        const sitemapTag = BRAND_ATTRIBUTES[brand].sitemap;
        const bbtTag = BRAND_ATTRIBUTES[brand].bbt;
        const prefixesArrToExclude = this.getPrefixesArrToExclude(
            prefixes,
            sitemapTag
        );

        if (
            this.validatePrefixesAgainstSiteMapAndBbtTag(
                prefixes,
                sitemapTag,
                bbtTag
            )
        ) {
            let arrAb: any[] = [];
            prefixesArrToExclude.forEach(prefix => {
                redisCacheResponse.forEach(
                    cacheData =>
                        cacheData[prefix] && arrAb.push(cacheData[prefix])
                );
            });

            arrAb = arrAb && arrAb.flat();
            const abTopics: any = {};
            arrAb.forEach(ab => {
                if (ab) abTopics[ab.topicId] = true;
            });

            cacheDataForPrefixes =
                prefixes === sitemapTag
                    ? cacheDataForPrefixes.map(arrAf => {
                          return arrAf?.map(af => {
                              af.noIndex = this.doesTopicIdExist(abTopics, af);
                              return af;
                          });
                      })
                    : cacheDataForPrefixes;

            cacheDataForPrefixes = this.getCacheDataForPrefixes(
                skipSitemapCheck,
                cacheDataForPrefixes,
                abTopics
            );
        }

        if (cacheDataForPrefixes) {
            cacheDataForPrefixes.forEach(resp => {
                resp?.forEach(res => {
                    res?.categories?.forEach((resCat: any) => {
                        resCat.categoryValues = resCat?.categoryValues.filter(
                            (set => (f: any) =>
                                !set.has(f.value) && set.add(f.value))(
                                new Set()
                            )
                        );
                    });
                });
                topics.push(resp);
            });
        }
        return topics.flat();
    }

    private getPrefixesArrToExclude(prefixes: string[], sitemapTag: any) {
        return prefixes === sitemapTag
            ? SUPPORT_HUB_PREFIXES_NOT_FOR_SITEMAP
            : SUPPORT_HUB_PREFIXES_NOT_FOR_BBT;
    }

    private validatePrefixesAgainstSiteMapAndBbtTag(
        prefixes: string[],
        sitemapTag: any,
        bbtTag: any
    ) {
        return prefixes === sitemapTag || prefixes === bbtTag;
    }

    private doesTopicIdExist(abTopics: any, af: Topic) {
        return abTopics[af.topicId] ? true : false;
    }

    private getCacheDataForPrefixes(
        skipSitemapCheck: undefined | boolean,
        cacheDataForPrefixes: Topic[][],
        abTopics: any
    ) {
        if (!skipSitemapCheck) {
            cacheDataForPrefixes = cacheDataForPrefixes.map(arrAf => {
                return arrAf?.filter(af => {
                    return !abTopics[af.topicId];
                });
            });
        }
        return cacheDataForPrefixes;
    }

    public async getAstuteCacheData(supportedMarket: string, brand: string) {
        const cachePrefixes = BRAND_ATTRIBUTES[brand].cache;
        return await Promise.all(
            cachePrefixes.map(async (prefix: string) => {
                return {
                    [prefix]: await cacheService.getFromRedis(
                        ASTUTE.SITE_MAP_KEY_PREFIX +
                            prefix +
                            supportedMarket +
                            '-' +
                            brand
                    ),
                };
            })
        );
    }

    public async getHrefLangsForSupportedMarkets(
        topicId: number,
        brand: string,
        languageRegionCode: string,
        topicUrlSegments?: string[]
    ): Promise<string[]> {
        const totalStart = Date.now();
        try {
            const countryCode = languageRegionCode.split('-')[1];
            const languages = ASTUTE.RTE_COUNTRY_LANGUAGES[countryCode];
            if (!languages || languages.length <= 1) {
                let segments = topicUrlSegments;
                if (!segments) {
                    const topic = await this.findTopicFromSiteMap(topicId, languageRegionCode, brand);
                    segments = topic?.topicAbsoluteSeo?.substring(1).split('/')
                        || this.deriveTopicUrlSegments(topic, languageRegionCode, brand);
                }
                if (segments) {
                    const baseUrl = siteMapService.getBaseUrl(languageRegionCode, brand);
                    const howTosTopicUrl = siteMapService
                        .getPathFromRouterConfigs(
                            languageRegionCode,
                            brand,
                            'AstuteTopic',
                            segments
                        );
                    const url = howTosTopicUrl && `${baseUrl.domain}${baseUrl.root}${howTosTopicUrl}`;
                    console.log(`getHrefLangsForSupportedMarkets single market url: ${url}`)
                    if (url) {
                        return [`<link rel="alternate" hreflang="${languageRegionCode}" href="${url}"/>`];
                    }
                }
                console.log(`getHrefLangsForSupportedMarkets single-language country=${countryCode} topic not found for topicId=${topicId}`);
                return [];
            }

            const markets = languages.map(lang => `${lang}-${countryCode}`);

            const results = await Promise.all(
                markets.map(async market => {
                    try {
                        const topic = await this.findTopicFromSiteMap(topicId, market, brand);
                        const segments = topic?.topicAbsoluteSeo?.substring(1).split('/')
                            || this.deriveTopicUrlSegments(topic, market, brand);
                        if (!segments) {
                            console.log(`getHrefLangsForSupportedMarkets topicId=${topicId} NOT FOUND in sitemap for market=${market}`);
                            return '';
                        }
                        const baseUrl = siteMapService.getBaseUrl(market, brand);
                        const howTosTopicUrl = siteMapService
                            .getPathFromRouterConfigs(
                                market,
                                brand,
                                'AstuteTopic',
                                segments
                            );
                        const url = howTosTopicUrl && `${baseUrl.domain}${baseUrl.root}${howTosTopicUrl}`;
                        console.log(`getHrefLangsForSupportedMarkets multiple languages url generated: ${url}`);
                        if (!url) return '';
                        return `<link rel="alternate" hreflang="${market}" href="${url}"/>`;
                    } catch (e) {
                        console.error(`getHrefLangsForSupportedMarkets topicId=${topicId} FAILED for market=${market}: ${e}`);
                        return '';
                    }
                })
            );
            console.log(`getHrefLangsForSupportedMarkets topicId=${topicId} total=${Date.now() - totalStart}ms`);
            return results;
        } catch (error) {
            throw new Error(`getHrefLangsForSupportedMarkets - ${error}`);
        }
    }

    public async getTopicFromCache(
        market: string,
        brand: string,
        sitemapTag: string[],
        topicId: number
    ) {
        return (
            await this.getSitemapDataFromCache(market, sitemapTag, brand, true)
        ).find(topic => topic.topicId === topicId);
    }
    public async findCategoryFromSiteMap(
        categoryId: number,
        market: string,
        brand: string
    ): Promise<CategoryPage | undefined> {
        const prefixes: string[] = BRAND_ATTRIBUTES[brand].bbt;
        const excludeCatIds: number[] = BRAND_ATTRIBUTES[brand].excludeCatId;
        const languageCode = market.split('-')[0];

        let page = RTE_SITEMAP_PAGE_START;
        let totalPages = RTE_SITEMAP_INITIAL_TOTAL_PAGES;

        while (page < totalPages) {
            const response: SiteMapResponse = await this.rteService.getSiteMap(
                page,
                market,
                brand,
                RTE_SITEMAP_PAGE_SIZE
            );
            totalPages = response.totalPages;

            for (const topic of response.topics) {
                const categ = topic.categories?.find(
                    c => c.categoryId === categoryId
                );
                if (!categ || excludeCatIds.includes(categ.categoryId)) continue;

                const matchedPrefix = prefixes.find(prefix =>
                    categ.categoryName.startsWith(prefix)
                );
                if (!matchedPrefix) continue;

                const cleanedLocalizedName = categ.localizedCategoryName?.replace(
                    matchedPrefix,
                    ''
                );
                const categorySeo = this.sanitizeString(cleanedLocalizedName, languageCode) || '';
                const categoryAbsoluteSeo =
                    siteMapService.getPathFromRouterConfigs(
                        market,
                        brand,
                        'CategoryPageView',
                        [categorySeo]
                    ) || '';

                return {
                    categoryId: categ.categoryId,
                    categoryName: cleanedLocalizedName,
                    categorySeo,
                    categoryAbsoluteSeo,
                    subCategories: [],
                };
            }
            page++;
        }
        return undefined;
    }

    public async findSubCategoryFromSiteMap(
        subCategoryId: number,
        market: string,
        brand: string
    ): Promise<{ subCategoryAbsoluteSeo: string; subCategoryValue: string } | undefined> {
        const prefixes: string[] = BRAND_ATTRIBUTES[brand].bbt;
        const excludeCatIds: number[] = BRAND_ATTRIBUTES[brand].excludeCatId;
        const languageCode = market.split('-')[0];

        let page = RTE_SITEMAP_PAGE_START;
        let totalPages = RTE_SITEMAP_INITIAL_TOTAL_PAGES;

        while (page < totalPages) {
            const response: SiteMapResponse = await this.rteService.getSiteMap(
                page,
                market,
                brand,
                RTE_SITEMAP_PAGE_SIZE
            );
            totalPages = response.totalPages;

            for (const topic of response.topics) {
                for (const categ of topic.categories || []) {
                    if (excludeCatIds.includes(categ.categoryId)) continue;
                    const matchedPrefix = prefixes.find(prefix =>
                        categ.categoryName.startsWith(prefix)
                    );
                    if (!matchedPrefix) continue;

                    const subCat = categ.categoryValues.find(
                        cv => cv.categoryValueId === subCategoryId
                    );
                    if (!subCat) continue;

                    const cleanedCategoryName = categ.localizedCategoryName?.replace(matchedPrefix, '');
                    const categorySeo = this.sanitizeString(cleanedCategoryName, languageCode) || '';
                    const subCategorySeo = this.sanitizeString(subCat.localizedCategoryValue, languageCode) || '';

                    const subCategoryAbsoluteSeo =
                        siteMapService.getPathFromRouterConfigs(
                            market,
                            brand,
                            'SubCategoryPageView',
                            [categorySeo, subCategorySeo]
                        ) || '';

                    return {
                        subCategoryAbsoluteSeo,
                        subCategoryValue: subCat.localizedCategoryValue,
                    };
                }
            }
            page++;
        }
        return undefined;
    }

    public async findTopicFromSiteMap(
        topicId: number,
        market: string,
        brand: string
    ): Promise<Topic | undefined> {

        let page = RTE_SITEMAP_PAGE_START;
        let totalPages = RTE_SITEMAP_INITIAL_TOTAL_PAGES;

        while (page < totalPages) {
            const response: SiteMapResponse = await this.rteService.getSiteMap(
                page,
                market,
                brand,
                RTE_SITEMAP_PAGE_SIZE
            );
            totalPages = response.totalPages;

            const found = response.topics.find(t => t.topicId === topicId);
            if (found) return found;

            page++;
        }
        return undefined;
    }

    private deriveTopicUrlSegments(
        topic: Topic | undefined,
        market: string,
        brand: string
    ): string[] | undefined {
        if (!topic?.topicLabel || !topic.categories?.length) return undefined;
        const languageCode = market.split('-')[0];
        const bbtPrefixes = BRAND_ATTRIBUTES[brand].bbt;
        const category = topic.categories.find(
            cat => bbtPrefixes.some(prefix => cat.categoryName.startsWith(prefix))
                && cat.categoryValues?.length > 0
        ) || topic.categories[0];
        const categoryValue =
            category.categoryValues?.length > 0
                ? category.categoryValues[0]
                : null;
        if (!categoryValue) return undefined;

        const strippedCategoryName = (category.localizedCategoryName || category.categoryName)
            .replace(SUPPORT_HUB_REGEX.ARTICLE_CATEGORY_NAME_REGEX, '');
        const categorySeo = this.sanitizeString(strippedCategoryName, languageCode);
        const subCategorySeo = this.sanitizeString(categoryValue.localizedCategoryValue, languageCode);
        const topicLabelSeo = this.sanitizeString(topic.topicLabel, languageCode);

        if (!categorySeo || !subCategorySeo || !topicLabelSeo) return undefined;
        return [categorySeo, subCategorySeo, topicLabelSeo];
    }

    public async getHrefLangsForCategory(
        categoryId: number,
        brand: string,
        languageRegionCode: string
    ): Promise<string[]> {
        const totalStart = Date.now();
        try {
            const countryCode = languageRegionCode.split('-')[1];
            const languages = ASTUTE.RTE_COUNTRY_LANGUAGES[countryCode];

            if (!languages || languages.length <= 1) {
                const baseUrl = siteMapService.getBaseUrl(languageRegionCode, brand);
                const category = await this.findCategoryFromSiteMap(
                    categoryId,
                    languageRegionCode,
                    brand
                );
                if (category?.categoryName && category?.categoryAbsoluteSeo) {
                    const url = `${baseUrl.domain}${baseUrl.root}${category.categoryAbsoluteSeo}`;
                    console.log(`[hrefLangs] single-language country=${countryCode} url=${url} took=${Date.now() - totalStart}ms`);
                    return [`<link rel="alternate" hreflang="${languageRegionCode}" href="${url}"/>`];
                }
                console.log(`[hrefLangs] single-language country=${countryCode} category not found, took=${Date.now() - totalStart}ms`);
                return [];
            }

            const markets = languages.map(lang => `${lang}-${countryCode}`);

            const results = await Promise.all(
                markets.map(async market => {
                    const marketStart = Date.now();
                    try {
                        const category = await this.findCategoryFromSiteMap(
                            categoryId,
                            market,
                            brand
                        );
                        console.log(`[hrefLangs] categoryId=${categoryId} market=${market} took ${Date.now() - marketStart}ms`);
                        if (!category?.categoryName || !category?.categoryAbsoluteSeo) {
                            return '';
                        }
                        const baseUrl = siteMapService.getBaseUrl(market, brand);
                        const url = `${baseUrl.domain}${baseUrl.root}${category.categoryAbsoluteSeo}`;
                        console.log(`[hrefLangs] multi-language country=${countryCode} market=${market} url=${url}`);
                        return `<link rel="alternate" hreflang="${market}" href="${url}"/>`;
                    } catch {
                        console.log(`[hrefLangs] categoryId=${categoryId} market=${market} FAILED after ${Date.now() - marketStart}ms`);
                        return '';
                    }
                })
            );
            console.log(`[hrefLangs] categoryId=${categoryId} total=${Date.now() - totalStart}ms`);
            return results;
        } catch (error) {
            console.error(`getHrefLangsForCategory - ${error} ${categoryId}`);
            throw new Error(`getHrefLangsForCategory - ${error}`);
        }
    }
    public async getCategoryFromCache(
        market: string,
        brand: string,
        sitemapTag: string[],
        categoryId: number
    ) {
        let category: Category = {
            categoryId: 0,
            categoryName: '',
            localizedCategoryName: '',
            categoryValues: [],
        };

        const sitemap = await this.getSitemapDataFromCache(
            market,
            sitemapTag,
            brand,
            true
        );
        sitemap.forEach(topic => {
            const categories = topic.categories?.find(
                cat => cat.categoryId === categoryId
            );
            if (categories) category = categories;
        });
        return category;
    }
    public async getHrefLangsForSubCategory(
        subCategoryId: number,
        brand: string,
        languageRegionCode: string
    ): Promise<string[]> {
        console.log(`getHrefLangsForSubCategory ENTER subCategoryId=${subCategoryId} market=${languageRegionCode}`);
        try {
            const countryCode = languageRegionCode.split('-')[1];
            const languages = ASTUTE.RTE_COUNTRY_LANGUAGES[countryCode];

            if (!languages || languages.length <= 1) {
                const subCatInfo = await this.findSubCategoryFromSiteMap(subCategoryId, languageRegionCode, brand);
                if (subCatInfo?.subCategoryAbsoluteSeo) {
                    const baseUrl = siteMapService.getBaseUrl(languageRegionCode, brand);
                    const url = `${baseUrl.domain}${baseUrl.root}${subCatInfo.subCategoryAbsoluteSeo}`;
                    console.log(`getHrefLangsForSubCategory single-language url: ${url}`);
                    return [`<link rel="alternate" hreflang="${languageRegionCode}" href="${url}"/>`];
                }
                console.log(`getHrefLangsForSubCategory single-language: subcat not found id=${subCategoryId}`);
                return [];
            }

            const markets = languages.map(lang => `${lang}-${countryCode}`);

            const results = await Promise.all(
                markets.map(async market => {
                    try {
                        const subCatInfo = await this.findSubCategoryFromSiteMap(subCategoryId, market, brand);
                        if (!subCatInfo?.subCategoryAbsoluteSeo) return '';
                        const baseUrl = siteMapService.getBaseUrl(market, brand);
                        const url = `${baseUrl.domain}${baseUrl.root}${subCatInfo.subCategoryAbsoluteSeo}`;
                        console.log(`getHrefLangsForSubCategory multi-language url: ${url}`);
                        return `<link rel="alternate" hreflang="${market}" href="${url}"/>`;
                    } catch {
                        return '';
                    }
                })
            );
            return results;
        } catch (error) {
            console.error(
                `getHrefLangsForSubCategory - ${error} ${subCategoryId}`
            );
            throw new Error(`getHrefLangsForSubCategory - ${error}`);
        }
    }
    public async getSubCategoryFromCache(
        market: string,
        brand: string,
        sitemapTag: string[],
        subCategoryId: number
    ) {
        let subCategory: CategoryValue = {
            categoryValueId: 0,
            categoryValue: '',
            localizedCategoryValue: '',
        };

        const sitemap = await this.getSitemapDataFromCache(
            market,
            sitemapTag,
            brand,
            true
        );
        sitemap.forEach(topic => {
            topic.categories?.forEach(cat => {
                const subCategories = cat.categoryValues.find(
                    subcat => subcat.categoryValueId === subCategoryId
                );
                if (subCategories) subCategory = subCategories;
            });
        });
        return subCategory;
    }
    public async getSearchResults(
        searchterm: string,
        languageRegionCode: string,
        domain: string,
        root: string,
        brand: string
    ) {
        const topicPage: TopicPage = {
            topicLabel: '',
            topicHeading: '',
            categoryName: '',
            subCategoryName: '',
            bodyWoHeading: '',
            metaDescription: '',
            hrefLangs: '',
            relatedTopics: '',
            canonical: '',
            topicId: 0,
            categoryId: 0,
            sessionId: '',
        };

        try {
            const sessionID = await this.getSessionIdForSearch(
                languageRegionCode,
                brand
            );
            
            const searchResults = await this.rteArticleSearch(
                searchterm,
                sessionID,
                brand,
                languageRegionCode
            );
            topicPage.topicId = searchResults.intent && parseInt(searchResults.intent);
            topicPage.sessionId = sessionID;
            topicPage.topicLabel = searchResults.label;

            let privilegeContentData: PlaceholderResponse | undefined;
            if (topicPage.topicId) {
                try {
                    privilegeContentData = await this.astuteCallByTopicId(
                        topicPage.topicId,
                        languageRegionCode,
                        brand,
                        sessionID
                    );
                } catch (e) {
                    console.error(
                        `getSearchResults - privilege-content fallback - ${e} ${topicPage.topicId}`
                    );
                }
            }

            if (privilegeContentData?.categories && privilegeContentData.categories.length > 0) {
                const category = privilegeContentData.categories[0];
                topicPage.categoryId = category.categoryId;

                const categoryName = category.localizedCategoryName;
                topicPage.categoryName = categoryName.replace(SUPPORT_HUB_REGEX.ARTICLE_CATEGORY_NAME_REGEX, '');

                if (category.categoryValues && category.categoryValues.length > 0) {
                    topicPage.subCategoryName = category.categoryValues[0].localizedCategoryValue;
                }
            }

            if (!topicPage.categoryId) {
                topicPage.categoryId = 1;
            }

            let articleHtml: string;
            let faqSchemaSource: string;
            if (privilegeContentData?.response) {
                articleHtml = this.buildAdditionalTopicUrlsFromPrivilegeContent(
                    privilegeContentData,
                    root,
                    languageRegionCode,
                    brand
                );
                faqSchemaSource = articleHtml;
                articleHtml = this.sanitizeHtml(articleHtml);
            } else {
                faqSchemaSource = searchResults.utterance;
                articleHtml = this.sanitizeHtml(searchResults.utterance);
            }
            topicPage.faqSchema = extractFaqSchema(faqSchemaSource).faqSchema;

            const topicDataSeparated = this.getTopicBodyWoHeading(articleHtml);
            topicPage.bodyWoHeading = topicDataSeparated.topicData;
            topicPage.topicHeading = topicDataSeparated.topicHeading;

            const suggestedTopics =
                searchResults.suggestedTopics &&
                this.buildSuggestedTopicsFromSearchResponse(
                    searchResults.suggestedTopics,
                    domain,
                    root,
                    languageRegionCode,
                    brand
                );

            if (
                !topicPage.bodyWoHeading ||
                topicPage.bodyWoHeading
                    .replace(SUPPORT_HUB_REGEX.FIND_ALL_TABS_NEW_LINE, '')
                    .replace(SUPPORT_HUB_REGEX.ALL_MARKUP_TAGS, '')
                    .replace(SUPPORT_HUB_REGEX.NON_BREAKING_SPACE, '')
                    .length === 0
            ) {
                // Strip the heading from the fallback body so it does not
                // duplicate the separately-rendered topicHeading.
                topicPage.bodyWoHeading = this.getTopicBodyWoHeading(
                    this.sanitizeHtml(searchResults.utterance)
                ).topicData;
            }
            const translatedRoute = siteMapService.getPathFromRouterConfigs(
                languageRegionCode,
                brand,
                'AstuteSearch',
                [searchterm]
            );

            const currentRoot = root.substring(0, root.length - 1);
            const link = `https://${domain}${currentRoot}${translatedRoute}`;
            topicPage.bodyWoHeading = topicPage.bodyWoHeading?.replace(
                SUPPORT_HUB_REGEX.HREF_HASH,
                `href="${link}#`
            );
            let metaDescription;
            try {
                metaDescription = await this.getTopicAttributes(
                    brand,
                    topicPage.topicId,
                    languageRegionCode
                );
            } catch (e) {
                console.error(
                    `Inside getSearchResults - getTopicAttributes - ${e} ${topicPage.topicId}`
                );
            }
            topicPage.metaDescription = this.validateForMetaDescription(
                metaDescription,
                topicPage.bodyWoHeading
            );

            topicPage.canonical = `https://${domain}${currentRoot}${translatedRoute}`;

            if (suggestedTopics) {
                topicPage.relatedTopics = suggestedTopics.join(' ');
            }
        } catch (error) {
            console.error(`getSearchResults - ${error} ${searchterm}`);
            throw new Error(`getSearchResults - ${error} ${searchterm}`);
        }
        return topicPage;
    }

    public async buildSuggestedTopics(
        suggestedTopics: SuggestedTopic[],
        siteMapData: Topic[],
        root: string,
        languageRegionCode: string,
        brand: string
    ) {
        const markup: string[] = [];

        suggestedTopics.forEach(suggestedTopic => {
            const topic = siteMapData?.filter(
                topic => topic.topicId.toString() === suggestedTopic.intent
            );
            const currentRoot = root.substring(0, root.length - 1);
            const url =
                topic &&
                topic[0] &&
                `${currentRoot}${siteMapService
                    .getPathFromRouterConfigs(
                        languageRegionCode,
                        brand,
                        'AstuteTopic',
                        topic[0].topicAbsoluteSeo?.substring(1).split('/') || []
                    )
                    ?.concat(
                        topic[0].topicAbsoluteSeo?.endsWith('/') ? '/' : ''
                    )}`;
            const snippet = `<a href="${url}">${suggestedTopic.text}</a><br>`;
            url && markup.push(snippet);
        });
        return markup;
    }
    public buildSuggestedTopicsFromSearchResponse(
        suggestedTopics: SuggestedTopic[],
        domain: string,
        root: string,
        languageRegionCode: string,
        brand: string
    ): string[] {
        const markup: string[] = [];
        const currentRoot = root.substring(0, root.length - 1);
        const languageCode = languageRegionCode.split('-')[0];

        suggestedTopics.forEach(suggestedTopic => {
            if (
                suggestedTopic.categories &&
                suggestedTopic.categories.length > 0
            ) {
                const category = suggestedTopic.categories[0];
                const categoryValue =
                    category.categoryValues &&
                    category.categoryValues.length > 0
                        ? category.categoryValues[0]
                        : null;

                if (categoryValue) {
                    const strippedCategoryName = category.localizedCategoryName
                        .replace(SUPPORT_HUB_REGEX.ARTICLE_CATEGORY_NAME_REGEX, '');
                    const categorySeo = this.sanitizeString(
                        strippedCategoryName,
                        languageCode
                    );
                    const subCategorySeo = this.sanitizeString(
                        categoryValue.localizedCategoryValue,
                        languageCode
                    );
                    const topicLabelSeo = this.sanitizeString(
                        suggestedTopic.topicLabel ?? '',
                        languageCode
                    );

                    const topicAbsoluteSeo = `/${categorySeo}/${subCategorySeo}/${topicLabelSeo}`;
                    const topicPath = siteMapService
                        .getPathFromRouterConfigs(
                            languageRegionCode,
                            brand,
                            'AstuteTopic',
                            topicAbsoluteSeo.substring(1).split('/')
                        )
                        ?.concat('');

                    if (topicPath) {
                        const url = `${currentRoot}${topicPath}`;
                        const snippet = `<a href="${url}">${suggestedTopic.text}</a><br>`;
                        markup.push(snippet);
                    }
                }
            }
        });
        return markup;
    }

    public async getAstuteArticlesFromCache(market: string, brand: string) {
        const sitemapTag = BRAND_ATTRIBUTES[brand].sitemap;
        const categories = await this.getAllCategories(
            market,
            sitemapTag,
            brand,
            true
        );
        return categories.map(cat => {
            return {
                categoryName: cat.categoryName,
                categoryId: cat.categoryId,
                subCategories: cat.subCategories.map(subcat => {
                    return {
                        subCategoryName: subcat.subCategoryName,
                        subCategoryId: subcat.subCategoryId,
                        topics:
                            subcat.allTopics &&
                            subcat.allTopics.map(topic => {
                                return {
                                    topicLabel: topic.topicLabel,
                                    topicAbsoluteSeo: topic.topicAbsoluteSeo,
                                    topicId: topic.topicId,
                                    topicPreview: 'Coming soon...',
                                    oldTopicLabels: topic.oldTopicLabels,
                                };
                            }),
                    };
                }),
            };
        });
    }
    public async getAllCategories(
        languageRegionCode: string,
        prefixes: string[],
        brand: string,
        skipSitemapCheck?: boolean,
        skipCategory?: boolean
    ) {
        const categories: CategoryPage[] = [];
        const subCategories: SubCategoryPage[] = [];

        const brandAndLanguageRegionCode = {
            brand,
            languageRegionCode,
        };
        try {

            const siteMapData: SiteMapResponse = await this.getRteSiteMap(
                0,
                languageRegionCode,
                brand,
            );

            siteMapData.topics.forEach(topic => {
                const topicWoCategories = {
                    topicId: topic.topicId,
                    topicName: this.getTopicName(topic),
                    topicLabel: topic.topicLabel,
                    topicAbsoluteSeo: this.getTopicAbsoluteSeo(topic),
                    topicLabelSeo: this.getTopicLabelSeo(topic),
                    oldTopicLabels: this.getOldTopicLabels(topic),
                };
                topic.categories?.forEach(categ => {
                    const matchedPrefix = prefixes.find(prefix =>
                        categ.categoryName?.startsWith(prefix)
                    );
                    if (!matchedPrefix) return;

                    const strippedLocalizedName = categ.localizedCategoryName?.replace(
                        matchedPrefix,
                        ''
                    );

                    categ.categoryValues.forEach(categVal => {
                        const topicSplit = topic.topicAbsoluteSeo?.split('/');

                        const subCategory: SubCategoryPage = {
                            subCategorySeo: this.getSubCategorySeoFromCategoryValueSeo(
                                categVal
                            ),
                            subCategoryAbsoluteSeo:
                                siteMapService
                                    .getPathFromRouterConfigs(
                                        languageRegionCode,
                                        brand,
                                        'SubCategoryPageView',
                                        categVal.categoryValueAbsoluteSeo
                                            ?.substring(1)
                                            .split('/') || []
                                    )
                                    ?.concat(
                                        this.formatCategoryValueAbsoluteSeo(
                                            categVal
                                        )
                                    ) || '',
                            subCategoryId: categVal.categoryValueId,
                            subCategoryName: categVal.localizedCategoryValue,
                            allTopics: [],
                        };

                        const existingSubCat = subCategories.find(
                            subcat =>
                                subcat.subCategoryId ===
                                subCategory.subCategoryId
                        );

                        this.processSubCategories(
                            existingSubCat,
                            topicSplit,
                            subCategory,
                            topicWoCategories,
                            brandAndLanguageRegionCode,
                            topic,
                            subCategories
                        );

                        const languageCode = languageRegionCode.split('-')[0];
                        const strippedCategoryName = strippedLocalizedName?.replace(
                            SUPPORT_HUB_REGEX.ARTICLE_CATEGORY_NAME_REGEX,
                            ''
                        );
                        const derivedCategorySeo = categ.localizedCategoryNameSeo
                            || this.sanitizeString(strippedCategoryName, languageCode)
                            || '';
                        const categPage: CategoryPage = {
                            categoryId: categ.categoryId,
                            categoryName: strippedLocalizedName,
                            subCategories: [],
                            categorySeo: derivedCategorySeo,
                            categoryAbsoluteSeo:
                                categ.categoryAbsoluteSeo
                                    ? siteMapService
                                          .getPathFromRouterConfigs(
                                              languageRegionCode,
                                              brand,
                                              'CategoryPageView',
                                              categ.categoryAbsoluteSeo
                                                  .substring(1)
                                                  .split('/')
                                          )
                                          ?.concat(
                                              this.formatCategoryAbsoluteSeo(categ)
                                          ) || ''
                                    : siteMapService
                                          .getPathFromRouterConfigs(
                                              languageRegionCode,
                                              brand,
                                              'CategoryPageView',
                                              [derivedCategorySeo]
                                          ) || '',
                        };
                        const existingCat = categories.find(
                            cat => cat.categoryId === categ.categoryId
                        );
                        const existingSubCatInCat = categories.find(cat => {
                            return cat.subCategories.find(subCat => {
                                return (
                                    subCat.subCategoryId ===
                                    subCategory.subCategoryId
                                );
                            });
                        });

                        this.populateCategoriesAndSubCategories(
                            existingCat,
                            categPage,
                            subCategory,
                            categories,
                            existingSubCatInCat
                        );
                    });
                });
            });
            categories.forEach(categ => {
                categ.subCategories = [...categ.subCategories].sort((a, b) =>
                    a.subCategoryName
                        ?.toLocaleUpperCase()
                        ?.localeCompare(b.subCategoryName?.toLocaleUpperCase())
                );
                categ.subCategories.forEach(
                    subcat =>
                        (subcat.allTopics = subcat.allTopics?.sort((a, b) =>
                            a.topicLabel
                                ?.toLocaleUpperCase()
                                ?.localeCompare(
                                    b.topicLabel?.toLocaleUpperCase()
                                )
                        ))
                );
            });
        } catch (error) {
            console.error(error);
            throw new Error(`getAllCategories: ${error}`);
        }
        const filteredCategories = this.getFilteredCategories(
            skipCategory,
            categories,
            brand
        );

        return filteredCategories.sort((a, b) =>
            a.categoryName
                ?.toLocaleUpperCase()
                .localeCompare(b.categoryName?.toLocaleUpperCase())
        );
    }

    private populateCategoriesAndSubCategories(
        existingCat: CategoryPage | undefined,
        categPage: CategoryPage,
        subCategory: SubCategoryPage,
        categories: CategoryPage[],
        existingSubCatInCat: CategoryPage | undefined
    ) {
        if (!existingCat) {
            categPage.subCategories.push(subCategory);
            categories.push(categPage);
        } else if (!existingSubCatInCat)
            existingCat.subCategories.push(subCategory);
    }

    private processSubCategories(
        existingSubCat: SubCategoryPage | undefined,
        topicSplit: string[] | undefined,
        subCategory: SubCategoryPage,
        topicWoCategories: {
            topicId: number;
            topicLabel: string;
            topicLabelSeo: string;
            oldTopicLabels: string[];
            topicName: string;
            topicAbsoluteSeo: string;
        },
        brandAndLanguageRegionCode: BrandAndLanguageRegionCode,
        topic: Topic,
        subCategories: SubCategoryPage[]
    ) {
        if (!existingSubCat) {
            this.populateSubCategoryTopics(
                topicSplit,
                subCategory,
                topicWoCategories,
                brandAndLanguageRegionCode.languageRegionCode,
                brandAndLanguageRegionCode.brand,
                topic,
                subCategories
            );
        } else if (
            topicSplit &&
            existingSubCat.subCategorySeo === topicSplit[2]
        ) {
            topicWoCategories.topicAbsoluteSeo =
                siteMapService
                    .getPathFromRouterConfigs(
                        brandAndLanguageRegionCode.languageRegionCode,
                        brandAndLanguageRegionCode.brand,
                        'AstuteTopic',
                        topic.topicAbsoluteSeo?.substring(1).split('/') || []
                    )
                    ?.concat(this.formatTopicAbsoluteSeo(topic)) || '';
            existingSubCat.allTopics &&
                existingSubCat.allTopics.push(topicWoCategories);
        }
    }

    private getFilteredCategories(
        skipCategory: undefined | boolean,
        categories: CategoryPage[],
        brand: string
    ) {
        const filteredCategories =
            skipCategory && categories !== null && categories !== undefined
                ? categories?.filter(
                      categ =>
                          !BRAND_ATTRIBUTES[
                              brand
                          ].excludeCatId.includes(categ.categoryId)
                  )
                : categories;
        return filteredCategories;
    }

    private formatCategoryAbsoluteSeo(categ: Category) {
        return categ.categoryAbsoluteSeo?.endsWith('/') ? '/' : '';
    }

    private formatTopicAbsoluteSeo(topic: Topic) {
        return topic.topicAbsoluteSeo?.endsWith('/') ? '/' : '';
    }

    private formatCategoryValueAbsoluteSeo(categVal: CategoryValue) {
        return categVal.categoryValueAbsoluteSeo?.endsWith('/') ? '/' : '';
    }

    private populateSubCategoryTopics(
        topicSplit: string[] | undefined,
        subCategory: SubCategoryPage,
        topicWoCategories: {
            topicId: number;
            topicLabel: string;
            topicLabelSeo: string;
            oldTopicLabels: string[];
            topicName: string;
            topicAbsoluteSeo: string;
        },
        languageRegionCode: string,
        brand: string,
        topic: Topic,
        subCategories: SubCategoryPage[]
    ) {
        const topicAbsoluteSeo = topic.topicAbsoluteSeo?.endsWith('/')
            ? '/'
            : '';
        if (topicSplit && subCategory.subCategorySeo === topicSplit[2]) {
            topicWoCategories.topicAbsoluteSeo =
                siteMapService
                    .getPathFromRouterConfigs(
                        languageRegionCode,
                        brand,
                        'AstuteTopic',
                        topic.topicAbsoluteSeo?.substring(1).split('/') || []
                    )
                    ?.concat(topicAbsoluteSeo) || '';
            subCategory.allTopics &&
                subCategory.allTopics.push(topicWoCategories);
        }
        subCategories.push(subCategory);
    }

    private getSubCategorySeoFromCategoryValueSeo(categVal: CategoryValue) {
        return categVal.localizedCategoryValueSeo || '';
    }

    public async getAllFilteredCatAndSubcat(
        languageRegionCode: string,
        prefixes: string[],
        brand: string,
        category: string,
        subcategory?: string,
        skipSitemapCheck?: boolean,
        skipCategory?: boolean
    ): Promise<CatSubcatFilteredResponse> {
        try {
            const categories = await this.getAllCategories(
                languageRegionCode,
                prefixes,
                brand,
                skipSitemapCheck,
                skipCategory
            );
            const filteredCategories = categories?.find(
                categ => categ.categorySeo === category
            );
            const filteredSubCategories = filteredCategories?.subCategories?.find(
                subCateg => subCateg.subCategorySeo === subcategory
            );
            return {
                categories,
                filteredCategories,
                filteredSubCategories,
            };
        } catch (error) {
            console.error(error);
            throw new Error(`getAllFilteredCatAndSubcat: ${error}`);
        }
    }

    public async getCategoriesFromSiteMap(
        languageRegionCode: string,
        brand: string
    ): Promise<CategoryPage[]> {
        
        try {
            const siteMapResponse: SiteMapResponse = await this.rteService.getSiteMap(
                RTE_SITEMAP_PAGE_START,
                languageRegionCode,
                brand,
                RTE_SITEMAP_FETCH_SIZE
            );

            const categoryMap = new Map<number, CategoryPage>();
            const prefixes: string[] = BRAND_ATTRIBUTES[brand].bbt;
            const excludeCatIds: number[] = BRAND_ATTRIBUTES[brand]
                .excludeCatId;

            const languageCode = languageRegionCode.split('-')[0];

            siteMapResponse.topics.forEach(topic => {
                topic.categories?.forEach(categ => {
                    const matchedPrefix = prefixes.find(prefix =>
                        categ.categoryName.startsWith(prefix)
                    );

                    if (
                        matchedPrefix &&
                        !categoryMap.has(categ.categoryId) &&
                        !excludeCatIds.includes(categ.categoryId)
                    ) {
                        const cleanedLocalizedName = categ.localizedCategoryName?.replace(
                            matchedPrefix,
                            ''
                        );
                        const categorySeo = this.sanitizeString(
                            cleanedLocalizedName,
                            languageCode
                        ) || '';
                        const categoryAbsoluteSeo =
                            siteMapService.getPathFromRouterConfigs(
                                languageRegionCode,
                                brand,
                                'CategoryPageView',
                                [categorySeo]
                            ) || '';
                        categoryMap.set(categ.categoryId, {
                            categoryId: categ.categoryId,
                            categoryName: cleanedLocalizedName,
                            categorySeo,
                            categoryAbsoluteSeo,
                            subCategories: [],
                        });
                    }
                });
            });

            return Array.from(categoryMap.values()).sort((a, b) =>
                a.categoryName
                    ?.toLocaleUpperCase()
                    .localeCompare(b.categoryName?.toLocaleUpperCase())
            );
        } catch (error) {
            console.error(error);
            throw new Error(`getCategoriesFromSiteMap: ${error}`);
        }
    }

    public async getAllFilteredCatAndSubcatV2(
        languageRegionCode: string,
        brand: string,
        categorySeoSlug: string,
        subcategorySeoSlug?: string
    ): Promise<CatSubcatFilteredResponse> {
        try {
            const categories = await this.getCategoriesFromSiteMap(
                languageRegionCode,
                brand
            );

            const filteredCategory = categories.find(
                categ => categ.categorySeo === categorySeoSlug
            );

            if (!filteredCategory) {
                return { categories };
            }

            const categoryBbtResponse = await this.rteService.getCategoryBbt(
                filteredCategory.categoryId,
                languageRegionCode,
                brand
            );

            const languageCode = languageRegionCode.split('-')[0];
            const subCategories: SubCategoryPage[] = (categoryBbtResponse.subcategories || []).map(
                (subcat: RteBbtSubCategory) => {
                    const subCategorySeo =
                        this.sanitizeString(
                            subcat.localizedCategoryValue,
                            languageCode
                        ) || '';

                    const subCategoryAbsoluteSeo =
                        siteMapService.getPathFromRouterConfigs(
                            languageRegionCode,
                            brand,
                            'SubCategoryPageView',
                            [
                                filteredCategory.categorySeo || '',
                                subCategorySeo,
                            ]
                        ) || '';

                    return {
                        subCategoryId: subcat.categoryValueId,
                        subCategoryName: subcat.localizedCategoryValue,
                        subCategorySeo,
                        subCategoryAbsoluteSeo,
                        allTopics: (subcat.topics || []).map(
                            (topic: Topic) => {
                                const topicLabelSeo =
                                    this.sanitizeString(
                                        topic.topicLabel,
                                        languageCode
                                    ) || '';
                                const topicAbsoluteSeo =
                                    siteMapService.getPathFromRouterConfigs(
                                        languageRegionCode,
                                        brand,
                                        'AstuteTopic',
                                        [
                                            filteredCategory.categorySeo || '',
                                            subCategorySeo,
                                            topicLabelSeo,
                                        ]
                                    ) || '';
                                return {
                                    topicId: topic.topicId,
                                    topicLabel: topic.topicLabel,
                                    topicLabelSeo,
                                    topicAbsoluteSeo,
                                };
                            }
                        ),
                    } as SubCategoryPage;
                }
            );
            const hrefLangsStart = Date.now();
            const countryCode = languageRegionCode.split('-')[1];
            const marketLanguages: string[] | undefined = ASTUTE.RTE_COUNTRY_LANGUAGES[countryCode];
            const currentBaseUrl = siteMapService.getBaseUrl(languageRegionCode, brand);

            const hrefLangsMap = new Map<number, string[]>();
            const categoryHrefLangs: string[] = [];

            const currentCatUrl = `${currentBaseUrl.domain}${currentBaseUrl.root}${filteredCategory.categoryAbsoluteSeo}`;
            categoryHrefLangs.push(`<link rel="alternate" hreflang="${languageRegionCode}" href="${currentCatUrl}"/>`);
            for (const subcat of subCategories) {
                const subcatUrl = `${currentBaseUrl.domain}${currentBaseUrl.root}${subcat.subCategoryAbsoluteSeo}`;
                const link = `<link rel="alternate" hreflang="${languageRegionCode}" href="${subcatUrl}"/>`;
                hrefLangsMap.set(subcat.subCategoryId, [link]);
            }

            if (marketLanguages && marketLanguages.length > 1) {
                const otherMarkets = marketLanguages
                    .map(lang => `${lang}-${countryCode}`)
                    .filter(market => market !== languageRegionCode);

                console.log(`[hrefLangs-frontload] multi-language market (${countryCode}), fetching for: ${otherMarkets.join(', ')}`);

                const otherLanguageData = await Promise.all(
                    otherMarkets.map(async market => {
                        const marketStart = Date.now();
                        try {
                            const [bbtResponse, categoryInfo] = await Promise.all([
                                this.rteService.getCategoryBbt(filteredCategory.categoryId, market, brand),
                                this.findCategoryFromSiteMap(filteredCategory.categoryId, market, brand)
                            ]);
                            console.log(`[hrefLangs-frontload] fetched data for market=${market} took=${Date.now() - marketStart}ms`);
                            return { market, bbtResponse, categoryInfo };
                        } catch (e) {
                            console.error(`[hrefLangs-frontload] failed for market=${market}: ${e}`);
                            return { market, bbtResponse: null, categoryInfo: null };
                        }
                    })
                );

                for (const { market, bbtResponse, categoryInfo } of otherLanguageData) {
                    if (!bbtResponse || !categoryInfo?.categorySeo) continue;
                    const marketBaseUrl = siteMapService.getBaseUrl(market, brand);
                    const marketLangCode = market.split('-')[0];

                    const catUrl = `${marketBaseUrl.domain}${marketBaseUrl.root}${categoryInfo.categoryAbsoluteSeo}`;
                    categoryHrefLangs.push(`<link rel="alternate" hreflang="${market}" href="${catUrl}"/>`);
                    console.log(`[hrefLangs-frontload] category link: lang=${market} url=${catUrl}`);

                    for (const bbtSubcat of bbtResponse.subcategories || []) {
                        const subCategorySeo = this.sanitizeString(bbtSubcat.localizedCategoryValue, marketLangCode) || '';
                        const subCategoryAbsoluteSeo = siteMapService.getPathFromRouterConfigs(
                            market, brand, 'SubCategoryPageView',
                            [categoryInfo.categorySeo, subCategorySeo]
                        ) || '';
                        const subcatUrl = `${marketBaseUrl.domain}${marketBaseUrl.root}${subCategoryAbsoluteSeo}`;
                        const link = `<link rel="alternate" hreflang="${market}" href="${subcatUrl}"/>`;

                        if (!hrefLangsMap.has(bbtSubcat.categoryValueId)) {
                            hrefLangsMap.set(bbtSubcat.categoryValueId, []);
                        }
                        hrefLangsMap.get(bbtSubcat.categoryValueId)!.push(link);
                    }
                }
            }

            for (const subcat of subCategories) {
                const links = hrefLangsMap.get(subcat.subCategoryId);
                if (links) {
                    subcat.hrefLangs = links.join(' ');
                }
            }

            console.log(`[hrefLangs-frontload] completed: ${subCategories.length} subcategories, ${hrefLangsMap.size} with hrefLangs, hrefLangs took=${Date.now() - hrefLangsStart}ms`);

            const filteredCategories: CategoryPage = {
                ...filteredCategory,
                subCategories,
                hrefLangs: categoryHrefLangs.join(' '),
            };

            const filteredSubCategories = subcategorySeoSlug
                ? subCategories.find(
                      subCat => subCat.subCategorySeo === subcategorySeoSlug
                  )
                : undefined;

            cacheBbtTopicIds(languageRegionCode, brand, categorySeoSlug, filteredCategories);

            return {
                categories,
                filteredCategories,
                filteredSubCategories,
            };
        } catch (error) {
            console.error(error);
            throw new Error(`getAllFilteredCatAndSubcatV2: ${error}`);
        }
    }

    public async getSubcategoryPageContent(
        languageRegionCode: string,
        brand: string,
        categorySeoSlug: string,
        subcategorySeoSlug?: string
    ): Promise<CatSubcatFilteredResponse> {
        try {
            const totalStart = Date.now();
            const prefixes: string[] = BRAND_ATTRIBUTES[brand].bbt;
            const excludeCatIds: number[] = BRAND_ATTRIBUTES[brand]
                .excludeCatId;
            const languageCode = languageRegionCode.split('-')[0];

            let page = RTE_SITEMAP_PAGE_START;
            let totalPages = RTE_SITEMAP_INITIAL_TOTAL_PAGES;
            let filteredCategory: CategoryPage | undefined;

            console.log(`[SubcatContent] start slug=${categorySeoSlug} locale=${languageRegionCode}`);

            // Paginate only until we find the target category
            while (page < totalPages && !filteredCategory) {
                const response: SiteMapResponse = await this.rteService.getSiteMap(
                    page,
                    languageRegionCode,
                    brand,
                    RTE_SITEMAP_PAGE_SIZE
                );
                totalPages = response.totalPages;

                for (const topic of response.topics) {
                    if (filteredCategory) break;
                    topic.categories?.forEach(categ => {
                        if (filteredCategory) return;
                        const matchedPrefix = prefixes.find(prefix =>
                            categ.categoryName.startsWith(prefix)
                        );
                        if (
                            matchedPrefix &&
                            !excludeCatIds.includes(categ.categoryId)
                        ) {
                            const cleanedLocalizedName = categ.localizedCategoryName?.replace(
                                matchedPrefix,
                                ''
                            );
                            const categorySeo =
                                this.sanitizeString(
                                    cleanedLocalizedName,
                                    languageCode
                                ) || '';
                            if (categorySeo === categorySeoSlug) {
                                const categoryAbsoluteSeo =
                                    siteMapService.getPathFromRouterConfigs(
                                        languageRegionCode,
                                        brand,
                                        'CategoryPageView',
                                        [categorySeo]
                                    ) || '';
                                filteredCategory = {
                                    categoryId: categ.categoryId,
                                    categoryName: cleanedLocalizedName,
                                    categorySeo,
                                    categoryAbsoluteSeo,
                                    subCategories: [],
                                };
                            }
                        }
                    });
                }
                page++;
            }

            if (!filteredCategory) {
                console.log(`[SubcatContent] category not found, total=${Date.now() - totalStart}ms`);
                return {};
            }

            const bbtStart = Date.now();
            const categoryBbtResponse = await this.rteService.getCategoryBbt(
                filteredCategory.categoryId,
                languageRegionCode,
                brand
            );
            console.log(`[SubcatContent] getCategoryBbt took=${Date.now() - bbtStart}ms, total=${Date.now() - totalStart}ms`);

            const subCategories: SubCategoryPage[] = (categoryBbtResponse.subcategories || []).map(
                (subcat: RteBbtSubCategory) => {
                    const subCategorySeo =
                        this.sanitizeString(
                            subcat.localizedCategoryValue,
                            languageCode
                        ) || '';

                    const subCategoryAbsoluteSeo =
                        siteMapService.getPathFromRouterConfigs(
                            languageRegionCode,
                            brand,
                            'SubCategoryPageView',
                            [
                                filteredCategory!.categorySeo || '',
                                subCategorySeo,
                            ]
                        ) || '';

                    return {
                        subCategoryId: subcat.categoryValueId,
                        subCategoryName: subcat.localizedCategoryValue,
                        subCategorySeo,
                        subCategoryAbsoluteSeo,
                        allTopics: (subcat.topics || []).map(
                            (topic: Topic) => {
                                const topicLabelSeo =
                                    this.sanitizeString(
                                        topic.topicLabel,
                                        languageCode
                                    ) || '';
                                const topicAbsoluteSeo =
                                    siteMapService.getPathFromRouterConfigs(
                                        languageRegionCode,
                                        brand,
                                        'AstuteTopic',
                                        [
                                            filteredCategory!.categorySeo || '',
                                            subCategorySeo,
                                            topicLabelSeo,
                                        ]
                                    ) || '';
                                return {
                                    topicId: topic.topicId,
                                    topicLabel: topic.topicLabel,
                                    topicLabelSeo,
                                    topicAbsoluteSeo,
                                };
                            }
                        ),
                    } as SubCategoryPage;
                }
            );

            const filteredCategories: CategoryPage = {
                ...filteredCategory,
                subCategories,
            };

            const filteredSubCategories = subcategorySeoSlug
                ? subCategories.find(
                      subCat => subCat.subCategorySeo === subcategorySeoSlug
                  )
                : undefined;

            return {
                filteredCategories,
                filteredSubCategories,
            };
        } catch (error) {
            console.error(error);
            throw new Error(`getSubcategoryPageContent: ${error}`);
        }
    }

    public async getAstuteFromCacheForRegion(
        market: string,
        brand: string
    ): Promise<AstuteCacheResponse[]> {
        if (serverSideService.isServerSide()) {
            return Promise.resolve(
                await this.getAstuteCacheData(market, brand)
            ) as Promise<AstuteCacheResponse[]>;
        } else {
            const params = {
                market: market,
                brand: brand,
            };

            const appConfig = ServiceHandler.AppConfigurationService.getAppConfiguration();

            const currentRoot =
                appConfig?.root && appConfig.root !== `/${brand}/${market}/`
                    ? appConfig?.root.substring(0, appConfig?.root.length - 1)
                    : '';

            const basePath = process.env.REACT_APP_REDIS_CACHE_URL
                ? process.env.REACT_APP_REDIS_CACHE_URL
                : currentRoot;

            return await HttpService.get<AstuteCacheResponse[]>(
                `${basePath}/redis/getAstuteFromCache`,
                true,
                { httpsAgent, params, proxy: false }
            )
                .then(result => {
                    return result.data;
                })
                .catch(e => {
                    console.error(e.message);
                    return e.message;
                });
        }
    }
    public async astuteCallByTopicId(
        topicId: number,
        languageRegionCode: string,
        brand: string,
        sessionID?: string
    ) {
        const supportedMarkets = BRAND_ATTRIBUTES[brand]
            .SUPPORTED_MARKETS_RTE_SEARCH;
        return supportedMarkets.includes(languageRegionCode)
            ? await this.rteService.rtePrivilegeContentCallByTopicId(topicId, sessionID)
            : await this.emplifiService.astuteCallByTopicId(
                  topicId,
                  languageRegionCode,
                  brand
              );
    }
    public async getRteSiteMap(
        pageNumber: number,
        languageRegionCode: string,
        brand: string
    ): Promise<SiteMapResponse> {
        const supportedMarkets = BRAND_ATTRIBUTES[brand]
            .SUPPORTED_MARKETS_RTE_DISCOVERABILITY;
        return supportedMarkets.includes(languageRegionCode)
            ? await this.rteService.getSiteMap(
                  pageNumber,
                  languageRegionCode,
                  brand,
                  RTE_SITEMAP_FETCH_SIZE
              )
            : await this.emplifiService.getSiteMap(
                  pageNumber,
                  languageRegionCode,
                  brand
              );
    }

    public async getSuggestedResults(
        languageRegionCode: string,
        currentSearchTerm: string,
        sessionID: string,
        brand: string
    ) {
        let returnSessionId = sessionID;

        if (!returnSessionId) {
            returnSessionId = await this.rteService.getSessionIdForSearch(
                languageRegionCode,
                brand
            );
        }
        return {
            suggestions: await this.rteService
                .getSuggestedResultsElastic(currentSearchTerm, returnSessionId)
                .catch(async () => {
                    returnSessionId = await this.rteService.getSessionIdForSearch(
                        languageRegionCode,
                        brand
                    );
                    return await this.rteService.getSuggestedResultsElastic(
                        currentSearchTerm,
                        returnSessionId
                    );
                }),
            sessionID: returnSessionId,
        };
    }
    public async getSessionIdForSearch(
        languageRegionCode: string,
        brand: string
    ): Promise<string> {
        languageRegionCode =
            languageRegionCode === 'fr-lu' ? 'fr-be' : languageRegionCode;
        const supportedMarkets = BRAND_ATTRIBUTES[brand]
            .SUPPORTED_MARKETS_RTE_SEARCH;
        return supportedMarkets.includes(languageRegionCode)
            ? await this.rteService.getSessionIdForSearch(
                  languageRegionCode,
                  brand
              )
            : await this.emplifiService.getSessionIdForSearch(
                  languageRegionCode,
                  brand
              );
    }
    public async rteArticleSearch(
        currentSearchTerm: string,
        sessionID: string,
        brand: string,
        languageRegionCode: string,
        questionID?: number
    ) {
        if (sessionID) {
            const supportedMarkets = BRAND_ATTRIBUTES[brand]
                .SUPPORTED_MARKETS_RTE_SEARCH;
            return supportedMarkets.includes(languageRegionCode)
                ? await this.rteService.rteArticleSearch(
                      currentSearchTerm,
                      sessionID
                  )
                : await this.emplifiService.astuteSearch(
                      currentSearchTerm,
                      sessionID,
                      brand,
                      questionID
                  );
        }
    }

    public async sendRteArticleFeedback(
        sessionID: string,
        topicId: number,
        rating: number,
        comments: string,
        languageRegionCode: string,
        brand: string
    ) {
        const supportedMarkets = BRAND_ATTRIBUTES[brand]
            .SUPPORTED_MARKETS_RTE_SEARCH;
        const newSessionID = await this.rteService.getSessionIdForSearch(
            languageRegionCode,
            brand
        );
        return supportedMarkets.includes(languageRegionCode)
            ? await this.rteService.sendRteArticleFeedback(
                newSessionID,
                  topicId,
                  rating,
                  comments
              )
            : await this.emplifiService.sendEmplifiFeedback(
                  sessionID,
                  topicId,
                  rating,
                  comments
              );
    }

    public async getTopicAttributes(
        brand: string,
        topicId: number,
        languageRegionCode: string
    ) {
        const supportedMarkets = BRAND_ATTRIBUTES[brand]
            .SUPPORTED_MARKETS_RTE_DISCOVERABILITY;
        return supportedMarkets.includes(languageRegionCode)
            ? await this.rteService.getTopicAttributes(
                  brand,
                  topicId,
                  languageRegionCode
              )
            : await this.emplifiService.getTopicAttributes(
                  brand,
                  topicId,
                  languageRegionCode
              );
    }
}
